import { useTranslation } from 'react-i18next';

import { formatDate, getCategoryColor, useMarketplaceProject } from '@codes/marketplace/marketplace';
import { Download, ExternalLink, FileCode, Github, X } from 'lucide-react';

import { Badge, Button, Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, Skeleton, cn } from '@codes/ui-kit';

interface ProjectDetailSheetProps {
    projectId: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const ProjectDetailSheet = ({ projectId, open, onOpenChange }: ProjectDetailSheetProps) => {
    const { t } = useTranslation();
    const { data: project, isLoading } = useMarketplaceProject(projectId || '');

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                className="overflow-y-scroll w-full sm:max-w-[540px]"
                aria-describedby={undefined}
                preventDismiss={false}
            >
                <SheetHeader className="justify-between w-full flex items-center" aria-describedby={undefined}>
                    <SheetTitle>{isLoading ? t('common.loading') : project?.title || ''}</SheetTitle>
                    <SheetClose>
                        <X className="w-5 h-5" />
                    </SheetClose>
                </SheetHeader>

                <div className="flex flex-col gap-y-6 py-6">
                    {isLoading ? (
                        <>
                            <Skeleton className="h-[200px] w-full rounded-lg" />
                            <div className="space-y-2">
                                <Skeleton className="h-5 w-full" />
                                <Skeleton className="h-5 w-full" />
                                <Skeleton className="h-5 w-3/4" />
                            </div>
                        </>
                    ) : project ? (
                        <>
                            {/* Preview Image */}
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-box">
                                {project.previewImage ? (
                                    <img
                                        src={project.previewImage}
                                        alt={project.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <FileCode className="w-16 h-16 text-text-400" />
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <h3 className="text-sm font-semibold text-text mb-2">
                                    {t('marketplace.detail.description')}
                                </h3>
                                <p className="text-sub text-sm leading-relaxed">{project.description}</p>
                            </div>

                            {/* Tags */}
                            <div>
                                <h3 className="text-sm font-semibold text-text mb-2">{t('marketplace.detail.tags')}</h3>
                                <div className="flex flex-wrap gap-2">
                                    <Badge className={cn('text-xs', getCategoryColor(project.category))}>
                                        {t(`marketplace.category.${project.category}`)}
                                    </Badge>
                                    {project.tags.map(tag => (
                                        <Badge key={tag} variant="outline" className="text-xs">
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Metadata */}
                            <div className="space-y-3 bg-box rounded-lg p-4">
                                <div className="flex justify-between">
                                    <span className="text-sm text-sub">{t('marketplace.detail.author')}</span>
                                    <span className="text-sm font-medium">{project.author}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-sub">{t('marketplace.detail.version')}</span>
                                    <span className="text-sm font-medium">v{project.version}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-sub">{t('marketplace.detail.updated')}</span>
                                    <span className="text-sm font-medium">{formatDate(project.updatedAt)}</span>
                                </div>
                            </div>

                            {/* External Links */}
                            {(project.githubUrl || project.demoUrl) && (
                                <div>
                                    <h3 className="text-sm font-semibold text-text mb-3">
                                        {t('marketplace.detail.links')}
                                    </h3>
                                    <div className="flex gap-3">
                                        {project.githubUrl && (
                                            <Button
                                                variant="outline"
                                                className="flex-1"
                                                onClick={() =>
                                                    window.open(project.githubUrl, '_blank', 'noopener,noreferrer')
                                                }
                                            >
                                                <Github className="w-4 h-4 mr-2" />
                                                GitHub
                                            </Button>
                                        )}
                                        {project.demoUrl && (
                                            <Button
                                                variant="outline"
                                                className="flex-1"
                                                onClick={() =>
                                                    window.open(project.demoUrl, '_blank', 'noopener,noreferrer')
                                                }
                                            >
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                Demo
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4 border-t">
                                <Button className="flex-1" disabled>
                                    <Download className="w-4 h-4 mr-2" />
                                    {t('marketplace.detail.install')}
                                </Button>
                            </div>

                            <p className="text-xs text-sub text-center">{t('marketplace.detail.installNote')}</p>
                        </>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-sub">{t('marketplace.detail.notFound')}</p>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
